import { useEffect, useState } from "react"

/**
 * `useStripeSetup` Hook
 *
 * This hook manages the Stripe setup process. It detects when Stripe's setup intent
 * redirect has completed and finalizes the payment method creation.
 * It allows for a custom creation function to be passed, making it versatile for different use cases.
 *
 * @param {function} [createFn] - A custom creation function to finalize the payment method setup.
 *
 * @returns {Object} - Contains the payment method, any errors, and loading state.
 *
 * createFn Signature:
 * function createFn(setupIntentId: string, setupIntentClientSecret: string): Promise
 *   - setupIntentId (string): The ID of the Stripe setup intent to finalize.
 *   - setupIntentClientSecret (string): The client secret Stripe returns on the
 *     redirect back from confirmSetup, for a createFn with no local setup-intent record.
 *   - Returns: A promise that resolves to the created payment method object.
 *
 * When using the `useStripeSetup` hook, if you wish to use a custom payment method creation process,
 * ensure that your function adheres to the above signature and return type.
 * This will ensure that the hook functions correctly with your custom logic.
 */
function useStripeSetup(createFn) {
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // This effect intentionally uses an empty dependency array to run exactly once on mount.
  // We only want to process URL params from a Stripe redirect when the page first loads.
  // The previous implementation stored URL params in React state, which created a race
  // condition: if the effect re-ran before the state update cleared the params, it could
  // trigger duplicate API calls. By using an empty dependency array and reading URL params
  // directly (not from state), we ensure the redirect is processed exactly once.
  //
  // Note: The backend CreatePaymentMethod service is idempotent, so duplicate calls are
  // safe, but we prefer to avoid unnecessary API calls.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const setupIntentId = params.get("setup_intent")
    const setupIntentClientSecret = params.get("setup_intent_client_secret")
    const redirectStatus = params.get("redirect_status")

    if (redirectStatus === "succeeded" && setupIntentId) {
      // Clear URL params immediately, before any async work
      window.history.replaceState(null, "", window.location.pathname)

      // Finalize the payment method setup
      setIsLoading(true)
      createFn(setupIntentId, setupIntentClientSecret)
        .then(setPaymentMethod)
        .catch(setError)
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    paymentMethod,
    error,
    isLoading,
  }
}

export default useStripeSetup
